import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useExamShell } from "../context/ExamShellContext";
import usePageBodyClass from "../utils/usePageBodyClass";
import "./Login.css";

const $ = window.$;

// Default Round 1 access code seeded the first time this page (or the Admin
// Dashboard) ever runs, so the portal works out of the box before an admin
// has explicitly set one from Settings.
const DEFAULT_ACCESS_CODE = "WELCOME1";

export default function Login() {
  usePageBodyClass("page-login");
  const navigate = useNavigate();
  const shell = useExamShell();
  const shellRef = useRef(shell);
  shellRef.current = shell;

  useEffect(() => {
    if (!localStorage.getItem("round1AccessCode")) {
      localStorage.setItem("round1AccessCode", DEFAULT_ACCESS_CODE);
    }

    // LIVE DATE & TIME CLOCK
    function updateLiveDateTime() {
      const now = new Date();
      const options = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      $("#liveDateTime").text(now.toLocaleDateString("en-US", options));
    }
    updateLiveDateTime();
    const clockTimer = setInterval(updateLiveDateTime, 1000);

    $("#candidateLoginForm").on("submit.pageLogin", function (e) {
      e.preventDefault();

      if (!shellRef.current.isFullscreen) {
        alert("You must enable Fullscreen Mode to proceed!");
        shellRef.current.requestFullscreen();
        return;
      }

      let isValid = true;

      $(".error-msg").hide();
      $(".form-control").removeClass("is-invalid");

      const nameVal = $("#fullName").val().trim();
      const nameRegex = /^[a-zA-Z\s]+$/;
      if (nameVal === "" || !nameRegex.test(nameVal)) {
        $("#fullName").addClass("is-invalid");
        $("#nameError").show();
        isValid = false;
      }

      const emailVal = $("#email").val().trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal)) {
        $("#email").addClass("is-invalid");
        $("#emailError").show();
        isValid = false;
      }

      const mobileVal = $("#mobile").val().trim();
      const mobileRegex = /^[0-9]{10}$/;
      if (!mobileRegex.test(mobileVal)) {
        $("#mobile").addClass("is-invalid");
        $("#mobileError").show();
        isValid = false;
      }

      const accessCodeVal = $("#accessCode").val().trim();
      const requiredCode = localStorage.getItem("round1AccessCode") || DEFAULT_ACCESS_CODE;
      if (accessCodeVal === "" || accessCodeVal.toUpperCase() !== requiredCode.toUpperCase()) {
        $("#accessCode").addClass("is-invalid");
        $("#accessCodeError").show();
        isValid = false;
      }

      if (isValid) {
        localStorage.setItem("candidateName", nameVal);
        localStorage.setItem("candidateEmail", emailVal);
        localStorage.setItem("candidatePhone", mobileVal);
        localStorage.setItem("examStatus", "locked");

        // Maintain the registered students directory used by the Admin Dashboard
        let students = [];
        try {
          students = JSON.parse(localStorage.getItem("registeredStudents") || "[]");
        } catch (err) {
          students = [];
        }
        const existingIdx = students.findIndex(
          (s) => (s.email || "").toLowerCase() === emailVal.toLowerCase()
        );
        const record = {
          fullName: nameVal,
          email: emailVal,
          phone: mobileVal,
          registeredAt: new Date().toISOString(),
        };
        if (existingIdx >= 0) students[existingIdx] = record;
        else students.push(record);
        localStorage.setItem("registeredStudents", JSON.stringify(students));

        navigate("/instructions");
      }
    });

    $("input").on("input.pageLogin", function () {
      if ($(this).hasClass("is-invalid")) {
        $(this).removeClass("is-invalid");
        $(this).closest(".mb-3, .mb-4").find(".error-msg").hide();
      }
    });

    // Anti-cheat: block copy/selection (contextmenu is already blocked globally
    // by the exam shell; F12 / Ctrl+Shift+I / Ctrl+U / Escape are too).
    $(document).on("selectstart.pageLogin copy.pageLogin paste.pageLogin cut.pageLogin dragstart.pageLogin", (e) => {
      e.preventDefault();
      return false;
    });

    return () => {
      clearInterval(clockTimer);
      $("#candidateLoginForm").off(".pageLogin");
      $("input").off(".pageLogin");
      $(document).off(".pageLogin");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="page-login">
      <div className="header-datetime-top">
        <i className="fa-regular fa-calendar-days me-2"></i>
        <span id="liveDateTime">Loading Date &amp; Time...</span>
      </div>

      <div className="login-card my-4">
        <div className="login-header">
          <i className="fa-solid fa-graduation-cap fa-2x mb-2 text-warning"></i>
          <h4>Online Exam Portal</h4>
          <span className="badge instruction-badge mt-1">Candidate Sign In</span>
        </div>

        <div className="login-body">
          <form id="candidateLoginForm" noValidate>
            <div className="mb-3">
              <label htmlFor="fullName" className="form-label fw-bold small text-secondary">
                Full Name
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa-solid fa-user text-muted"></i>
                </span>
                <input type="text" className="form-control" id="fullName" placeholder="John Doe" required />
              </div>
              <div className="error-msg" id="nameError">
                Please enter your full name (letters and spaces only).
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label fw-bold small text-secondary">
                Email Address
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa-solid fa-envelope text-muted"></i>
                </span>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  placeholder="candidate@example.com"
                  required
                />
              </div>
              <div className="error-msg" id="emailError">
                Please enter a valid email address.
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="mobile" className="form-label fw-bold small text-secondary">
                Mobile Number
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa-solid fa-phone text-muted"></i>
                </span>
                <input
                  type="tel"
                  className="form-control"
                  id="mobile"
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  required
                />
              </div>
              <div className="error-msg" id="mobileError">
                Please enter a valid 10-digit mobile number.
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="accessCode" className="form-label fw-bold small text-secondary">
                Access Code Provided
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fa-solid fa-key text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="accessCode"
                  placeholder="Enter the access code shared by your administrator"
                  autoComplete="off"
                  required
                />
              </div>
              <div className="error-msg" id="accessCodeError">
                Incorrect access code. Please check the code shared by your administrator.
              </div>
            </div>

            <button type="submit" className="btn btn-portal w-100 mb-3">
              <i className="fa-solid fa-right-to-bracket me-2"></i>Enter Exam Portal
            </button>

            <div className="text-center small text-muted">
              Need help? Contact system support or exam administrator.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
